<script setup lang="ts">
import { Check, ChevronDown } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import TheCalendar from '@/components/mode-list/TheCalendar.vue';
// @ts-expect-error unknown import
import '@vueform/slider/themes/default.css';

import { useFiltersStore } from '@/stores/useFiltersStore';

const props = defineProps<{
  placeholder: string;
  alignment?: 'left' | 'right';
}>();

const filtersStore = useFiltersStore();
const { filters, datesAsStrings } = storeToRefs(filtersStore);
const isOpen = ref(false);
const position = computed(() => props.alignment ? `${props.alignment}:0;` : 'left:0;');
const isFilterActive = computed(() => filters.value.filters.find((it: any) => it.filterProperties.type === 'date_range'));

function openDropdown() {
  if (isOpen.value)
    return;
  isOpen.value = true;
  setTimeout(() => {
    isOpen.value === true ? document?.addEventListener('click', handleOutsideClick) : document?.removeEventListener('click', handleOutsideClick);
    isOpen.value === true ? document?.addEventListener('keydown', handleKeyboardNavigation) : document?.removeEventListener('keydown', handleKeyboardNavigation);
  }, 100);
}

function closeDropdown() {
  isOpen.value = false;
  setTimeout(() => {
    isOpen.value === true ? document?.addEventListener('click', handleOutsideClick) : document?.removeEventListener('click', handleOutsideClick);
    isOpen.value === true ? document?.addEventListener('keydown', handleKeyboardNavigation) : document?.removeEventListener('keydown', handleKeyboardNavigation);
  }, 100);
}

function handleOutsideClick(event: Event) {
  const path = event.composedPath();
  const target = path[0];
  const dropdown = document.querySelector('yb-rdbk-list')?.shadowRoot?.getElementById('dates_dropdown');
  // @ts-expect-error typeof target
  if (!dropdown?.contains(target)) {
    closeDropdown();
  }
};

function handleKeyboardNavigation(event: Event) {
  const e = event as KeyboardEvent;
  if (e.key === 'Escape') {
    closeDropdown();
  }
}
</script>

<template>
  <div class="custom-dropdown" :aria-expanded="isOpen">
    <button
      :aria-haspopup="true"
      aria-controls="dropdown"
      :aria-expanded="isOpen"
      role="combobox"
      class="open-button"
      @click="openDropdown"
    >
      {{ datesAsStrings ?? props.placeholder }}<span v-if="isFilterActive" class="checked"><Check
        :size="14"
        color="var(--yb-color-white)"
      /></span><span
        class="svg" :class="{ rotate: isOpen }" aria-hidden="true"
      ><ChevronDown
        :size="20"
        color="var(--yb-color-black)"
      /></span>
    </button>
    <div v-show="isOpen" id="dates_dropdown" class="content-dropdown" :style="position" :aria-expanded="isOpen">
      <TheCalendar />
    </div>
  </div>
</template>

<style lang="css" scoped>
.custom-dropdown {
  position: relative;
  width: fit-content;
  font-size: var(--yb-text-sm);
  font-weight: var(--yb-text-light);
}

.open-button {
  display: flex;
  align-items: center;
  width: 100%;
  padding: var(--yb-spacing-xs) var(--yb-spacing-sm);
  background-color: var(--yb-color-white);
  color: var(--yb-color-black);
  border: 1px solid var(--yb-color-mediumgray);
  border-radius: var(--yb-radius-sm);
  outline-color: var(--yb-outline);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;

  .checked {
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: var(--yb-text-xs);
    font-weight: var(--yb-text-bold);
    background-color: var(--yb-color-black);
    color: var(--yb-color-white);
    width: 14px;
    aspect-ratio: 1;
    padding: 2px;
    margin-left: var(--yb-spacing-xs);
    border-radius: var(--yb-radius-full);
  }

  .svg {
    position: relative;
    top: 2px;
    margin-left: var(--yb-spacing-xs);

    &.rotate {
      transform: rotate(180deg);
      top: -2px;
    }

    @media (prefers-reduced-motion: no-preference) {
      transform: rotate(0);
      transition: transform 0.2s ease-out;
      transform-origin: center;
    }
  }
}

.content-dropdown {
  position: absolute;
  top: calc(100% + 5px);
  list-style-type: none;
  padding: var(--yb-spacing-sm);
  background-color: var(--yb-color-white);
  border-radius: var(--yb-radius-sm);
  box-shadow: var(--yb-color-mediumgray) 0px 4px 8px 0px;
  z-index: 10;

  @media (prefers-reduced-motion: no-preference) {
    opacity: 0;
    transform: translateY(-10px);
    animation: opening 0.2s ease-out forwards;
  }
}

@keyframes opening {
  to {
    opacity: 1;
    transform: translateY(0px);
  }
}
</style>
